/**
 * ModelWindowContent - Renders the real ModelDetailPage inside a tiled window.
 *
 * Wraps ModelDetailPage in CapabilityDetailProvider (which the route-level
 * CapabilityDetailPage normally provides) and passes appKey/name as props
 * so the page works without route params.
 *
 * ModelDetailPage itself pushes the action header layer via the headerLayerId
 * prop — it owns its own save/publish/edit logic (customSave). This component
 * only provides the CapabilityDetailProvider shell and the archive dialog.
 */

import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CapabilityDetailProvider } from '@/lib/capability-detail-context';
import { api } from '@/lib/api-client';
import { toast } from 'sonner';
import { Archive } from 'lucide-react';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import ModelDetailPage from '@/pages/models/model-detail-page';
import { useTiledWindows } from '@/components/tiled-windows';
import type { ModelWindow } from '@/types/tiled-window';

interface ModelWindowContentProps {
    window: ModelWindow;
}

export function ModelWindowContent({ window: win }: ModelWindowContentProps) {
    const appKey = win.appKey ?? '';
    const name = win.entityName;
    const headerLayerId = `window-${win.id}`;
    const queryClient = useQueryClient();
    const { closeWindow } = useTiledWindows();

    const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
    const [isArchiving, setIsArchiving] = useState(false);
    const openArchiveDialog = useCallback(() => setArchiveDialogOpen(true), []);

    const handleArchive = useCallback(async () => {
        if (!appKey || !name) return;
        setIsArchiving(true);
        try {
            await api.delete(`/applications/${appKey}/capabilities/${encodeURIComponent(name)}`);
            toast.success('Model archived successfully');
            queryClient.invalidateQueries({ queryKey: ['capabilities'] });
            closeWindow(win.id);
        } catch {
            toast.error('Failed to archive model');
        } finally {
            setIsArchiving(false);
            setArchiveDialogOpen(false);
        }
    }, [appKey, name, queryClient, closeWindow, win.id]);

    return (
        <CapabilityDetailProvider openArchiveDialog={openArchiveDialog}>
            <div className="h-full w-full overflow-auto">
                <ModelDetailPage appKey={appKey} name={name} headerLayerId={headerLayerId} />
            </div>

            <AlertDialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <Archive size={18} className="text-destructive" />
                            Archive Model
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to archive &quot;{name}&quot;? It will no longer
                            appear in the active model list.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={isArchiving}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => {
                                e.preventDefault();
                                void handleArchive();
                            }}
                            disabled={isArchiving}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {isArchiving ? 'Archiving...' : 'Archive'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </CapabilityDetailProvider>
    );
}
